import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// httpOnly cookies (admin_token / session_token) are sent automatically via withCredentials.
// No bearer tokens are read from localStorage — this mitigates XSS token theft.
const client = axios.create({ baseURL: API, timeout: 15000, withCredentials: true });

const safe = async (fn, fallback) => {
  try {
    const res = await fn();
    return res.data;
  } catch (e) {
    console.warn("API fallback used:", e?.message || e);
    return fallback;
  }
};

export const api = {
  // Public reads
  getBrand: (fb = null) => safe(() => client.get("/brand"), fb),
  getCategories: (fb = []) => safe(() => client.get("/categories"), fb),
  getTests: ({ cat = "", q = "" } = {}, fb = []) =>
    safe(() => client.get("/tests", { params: { cat: cat || undefined, q: q || undefined } }), fb),
  getTest: (id, fb = null) => safe(() => client.get(`/tests/${id}`), fb),
  getPackages: ({ q = "" } = {}, fb = []) =>
    safe(() => client.get("/packages", { params: { q: q || undefined } }), fb),
  getPackage: (id, fb = null) => safe(() => client.get(`/packages/${id}`), fb),
  getLocations: (fb = []) => safe(() => client.get("/locations"), fb),
  getLocation: (id, fb = null) => safe(() => client.get(`/locations/${id}`), fb),
  getBlogs: ({ q = "" } = {}, fb = []) =>
    safe(() => client.get("/blogs", { params: { q: q || undefined } }), fb),
  getBlog: (id, fb = null) => safe(() => client.get(`/blogs/${id}`), fb),
  getReviews: (fb = []) => safe(() => client.get("/reviews"), fb),

  // Public writes
  createBooking: (payload) => client.post("/bookings", payload).then((r) => r.data),
  createContact: (payload) => client.post("/contacts", payload).then((r) => r.data),
  requestOtp: (phone) => client.post("/report/request-otp", { phone }).then((r) => r.data),
  verifyOtp: (phone, otp) => client.post("/report/verify", { phone, otp }).then((r) => r.data),

  // Admin (auth via httpOnly cookie set by /admin/login)
  adminLogin: (username, password) => client.post("/admin/login", { username, password }).then((r) => r.data),
  adminLogout: () => client.post("/admin/logout").then((r) => r.data),
  adminMe: () => client.get("/admin/me").then((r) => r.data),
  adminStats: () => client.get("/admin/stats").then((r) => r.data),
  adminTapSummary: () => client.get("/admin/track/summary").then((r) => r.data),
  adminUpdateBrand: (payload) => client.put("/admin/brand", payload).then((r) => r.data),
  adminBookings: () => client.get("/admin/bookings").then((r) => r.data),
  adminContacts: () => client.get("/admin/contacts").then((r) => r.data),
  adminCreateTest: (p) => client.post("/admin/tests", p).then((r) => r.data),
  adminUpdateTest: (id, p) => client.put(`/admin/tests/${id}`, p).then((r) => r.data),
  adminDeleteTest: (id) => client.delete(`/admin/tests/${id}`).then((r) => r.data),
  adminCreatePackage: (p) => client.post("/admin/packages", p).then((r) => r.data),
  adminUpdatePackage: (id, p) => client.put(`/admin/packages/${id}`, p).then((r) => r.data),
  adminDeletePackage: (id) => client.delete(`/admin/packages/${id}`).then((r) => r.data),
  adminCreateBlog: (p) => client.post("/admin/blogs", p).then((r) => r.data),
  adminUpdateBlog: (id, p) => client.put(`/admin/blogs/${id}`, p).then((r) => r.data),
  adminDeleteBlog: (id) => client.delete(`/admin/blogs/${id}`).then((r) => r.data),
  adminUpdateLocation: (id, p) => client.put(`/admin/locations/${id}`, p).then((r) => r.data),

  // Uploads / patient reports
  uploadPrescription: (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/uploads/prescription", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
    }).then((r) => r.data);
  },
  adminListReports: (q = "") => client.get("/admin/reports", { params: { q: q || undefined } }).then((r) => r.data),
  adminUploadReport: (payload) => {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
    return client.post("/admin/reports/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  adminDeleteReport: (id) => client.delete(`/admin/reports/${id}`).then((r) => r.data),
  reportRequestOtp: (phone) => client.post("/report/request-otp", { phone }).then((r) => r.data),
  reportVerify: (phone, otp) => client.post("/report/verify", { phone, otp }).then((r) => r.data),
  reportDownloadUrl: (id, token) => `${API}/report/download/${id}?token=${encodeURIComponent(token)}`,
};

export default api;
